import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, of, throwError, timer } from 'rxjs';
import { catchError, expand, filter, map, switchMap, take } from 'rxjs/operators';
import {
  FeatureRequest,
  FeatureRequestDraft,
  FeatureRequestError,
  FeatureRequestNamed,
  FeatureRequestPage,
  FeatureRequestQuery,
  FeatureRequestTag,
  FeatureRequestThumbnail,
  FeatureRequestTranslationState,
} from './feature-request.model';

/** The error code with which the API refuses a reader who is not involved. */
const INVOLVEMENT_REQUIRED = 'ideas_involvement_required';

const JSON_API_MEDIA_TYPE = 'application/vnd.api+json';

/** What one request is about, asked for on the detail only. */
const DETAIL_INCLUDES = 'topics,tags,cultures';

type ResourceIdentifier = { type: string; id: string };

type ResourceObject = ResourceIdentifier & {
  attributes: Record<string, any>;
  relationships?: Record<string, { data?: ResourceIdentifier[] | null }>;
  meta?: Record<string, any>;
};

/**
 * Ideas from `/api/v1/feature-requests`: the list, one request, submitting,
 * withdrawing and reacting. JSON:API in and out; normalised here.
 *
 * What a reader may see is decided by the backend alone. A request they may
 * not see is a 404, exactly like one that does not exist.
 *
 * Failures are thrown as a {@link FeatureRequestError} carrying a key in the
 * `ideas` translation scope, so a component never reads an HTTP status.
 * Comments are not here: they are the conversation API's.
 */
@Injectable({
  providedIn: 'root',
})
export class FeatureRequestService {
  private http = inject(HttpClient);

  /** Where the backend names no interval of its own. */
  private static readonly POLL_INTERVAL_SECONDS = 5;

  /** Backing off without a ceiling turns a slow translation into a poll that arrives after the reader has left. */
  private static readonly POLL_MAXIMUM_INTERVAL_SECONDS = 30;

  /** Giving up is not a failure: the translation carries on, and the reader sees it next time. */
  private static readonly POLL_MAXIMUM_ATTEMPTS = 12;

  private readonly headers = new HttpHeaders({ Accept: JSON_API_MEDIA_TYPE, 'Content-Type': JSON_API_MEDIA_TYPE });

  getPage(query: FeatureRequestQuery): Observable<FeatureRequestPage> {
    return this.http
      .get<{ data: ResourceObject[]; meta: Record<string, any> }>('api/v1/feature-requests', {
        headers: this.headers,
        params: FeatureRequestService.toParams(query),
      })
      .pipe(
        map((document) => ({
          items: document.data.map((resource) => this.normalise(resource)),
          meta: {
            current_page: document.meta?.['current_page'] ?? 1,
            last_page: document.meta?.['last_page'] ?? 1,
            per_page: document.meta?.['per_page'] ?? 20,
            total: document.meta?.['total'] ?? 0,
          },
          canCreate: document.meta?.['can_create'] ?? { public: false, hubs: [] },
          hub: document.meta?.['hub'] ?? null,
          tags: (Array.isArray(document.meta?.['tags']) ? document.meta['tags'] : [])
            .filter((tag: Partial<FeatureRequestTag>) => typeof tag?.slug === 'string' && typeof tag?.name === 'string')
            .map((tag: FeatureRequestTag) => ({ slug: tag.slug, name: tag.name, count: Number(tag.count) || 0 })),
        })),
        catchError((error) => this.fail(error)),
      );
  }

  getOne(slug: string): Observable<FeatureRequest> {
    return this.http
      .get<{ data: ResourceObject; included?: ResourceObject[] }>('api/v1/feature-requests/' + encodeURIComponent(slug), {
        headers: this.headers,
        params: new HttpParams().set('include', DETAIL_INCLUDES),
      })
      .pipe(
        map((document) => this.normalise(document.data, document.included ?? [])),
        catchError((error) => this.fail(error)),
      );
  }

  /** Submit a request. It comes back pending: only its initiator and staff see it until it is approved. */
  submit(draft: FeatureRequestDraft): Observable<FeatureRequest> {
    const relationships = draft.hub ? { hub: { data: { type: 'hubs', id: draft.hub } } } : {};

    return this.http
      .post<{ data: ResourceObject }>(
        'api/v1/feature-requests',
        {
          data: {
            type: 'feature-requests',
            attributes: { name: draft.name.trim(), original_request: draft.originalRequest.trim() },
            relationships,
          },
        },
        { headers: this.headers },
      )
      .pipe(
        map((document) => this.normalise(document.data)),
        catchError((error) => this.fail(error)),
      );
  }

  /** The initiator takes a pending request back. */
  withdraw(slug: string): Observable<void> {
    return this.http
      .post<void>('api/v1/feature-requests/' + encodeURIComponent(slug) + '/withdrawal', null, { headers: this.headers })
      .pipe(catchError((error) => this.fail(error)));
  }

  /** Like a request, or take the like back. Answers with the request, its count updated. */
  like(slug: string, on: boolean): Observable<FeatureRequest> {
    const url = 'api/v1/feature-requests/' + encodeURIComponent(slug) + '/like';
    const request = on
      ? this.http.put<{ data: ResourceObject }>(url, null, { headers: this.headers })
      : this.http.delete<{ data: ResourceObject }>(url, { headers: this.headers });

    return request.pipe(
      map((document) => this.normalise(document.data)),
      catchError((error) => this.fail(error)),
    );
  }

  /**
   * Ask for the idea in `locale`. The backend claims each idea and language once, so asking
   * again while a translation waits queues nothing. Answers with the detail in that language
   * as far as it exists, and where its translation stands.
   */
  requestTranslation(slug: string, locale: string): Observable<FeatureRequest> {
    return this.http
      .post<{ data: ResourceObject; included?: ResourceObject[] }>(
        'api/v1/feature-requests/' + encodeURIComponent(slug) + '/translations/' + encodeURIComponent(locale),
        null,
        { headers: this.headers },
      )
      .pipe(
        map((document) => this.normalise(document.data, document.included ?? [])),
        catchError((error) => this.fail(error)),
      );
  }

  /**
   * Ask for the idea until its translation stops being worth waiting for, then emit it once.
   * Emits nothing if it never settles within the attempts allowed, and nothing if a poll
   * fails: the page already shows the idea as it was written. As news does.
   */
  watchTranslation(slug: string): Observable<FeatureRequest> {
    let attempts = 0;
    const poll = () => this.getOne(slug).pipe(catchError(() => of(null)));

    return timer(this.intervalFor(1)).pipe(
      switchMap(poll),
      expand((idea) => {
        attempts++;

        if (idea === null || !idea.worthPolling || attempts >= FeatureRequestService.POLL_MAXIMUM_ATTEMPTS) {
          return EMPTY;
        }

        return timer(this.intervalFor(attempts + 1)).pipe(switchMap(poll));
      }),
      filter((idea): idea is FeatureRequest => idea !== null && !idea.worthPolling),
      take(1),
    );
  }

  /**
   * Whether a page showing this idea should ask for its translation. Not only when the served
   * language differs: a French idea whose reason staff wrote in English owes a French reader
   * the reason, and the backend says so with `absent`.
   */
  static needsTranslation(idea: FeatureRequest): boolean {
    return idea.translationState === 'absent' || idea.translationState === 'failed';
  }

  /** Follow a request, or stop. Answers with the request. */
  follow(slug: string, on: boolean): Observable<FeatureRequest> {
    const url = 'api/v1/feature-requests/' + encodeURIComponent(slug) + '/follow';
    const request = on
      ? this.http.put<{ data: ResourceObject }>(url, null, { headers: this.headers })
      : this.http.delete<{ data: ResourceObject }>(url, { headers: this.headers });

    return request.pipe(
      map((document) => this.normalise(document.data)),
      catchError((error) => this.fail(error)),
    );
  }

  static toParams(query: FeatureRequestQuery): HttpParams {
    let params = new HttpParams();

    if (query.stages?.length) {
      params = params.set('filter[stage]', query.stages.join(','));
    }

    if (query.tags?.length) {
      params = params.set('filter[tag]', query.tags.map((slug) => 'feature:' + slug).join(','));
    }

    if (query.status?.length) {
      params = params.set('filter[status]', query.status.join(','));
    }

    if (query.hub) {
      params = params.set('filter[hub]', query.hub);
    }

    if (query.mine) {
      params = params.set('filter[mine]', 'true');
    }

    if (query.following) {
      params = params.set('filter[following]', 'true');
    }

    if (query.search && query.search.trim().length >= 3) {
      params = params.set('filter[search]', query.search.trim());
    }

    if (query.sort) {
      params = params.set('sort', query.sort);
    }

    if (query.page) {
      params = params.set('page[number]', String(query.page));
    }

    if (query.pageSize) {
      params = params.set('page[size]', String(query.pageSize));
    }

    return params;
  }

  /** The listing's picture, as the backend resolved it; anything unrecognised is `none`. */
  static thumbnail(raw: any): FeatureRequestThumbnail {
    switch (raw?.kind) {
      case 'image':
        return raw.image?.path
          ? { kind: 'image', source: raw.source === 'own' ? 'own' : 'topic', image: { disk: String(raw.image.disk), path: String(raw.image.path), alt_text: raw.image.alt_text ?? null } }
          : { kind: 'none' };
      case 'icon':
        return { kind: 'icon', iconType: raw.icon_type ?? null, iconIdentifier: raw.icon_identifier ?? null, imgUrl: raw.img_url ?? null };
      case 'flag':
        return raw.hash ? { kind: 'flag', hash: String(raw.hash), iso: raw.iso ?? null, name: raw.name ?? null } : { kind: 'none' };
      default:
        return { kind: 'none' };
    }
  }

  private normalise(resource: ResourceObject, included: ResourceObject[] = []): FeatureRequest {
    const a = resource.attributes ?? {};
    // A related record arrives as a reference in `relationships` and in full in `included`;
    // one that cannot be named is left out rather than shown by its slug.
    const named = (relationship: string): FeatureRequestNamed[] =>
      (resource.relationships?.[relationship]?.data ?? [])
        .map((reference) => included.find((candidate) => candidate.type === reference.type && candidate.id === reference.id))
        .filter((related): related is ResourceObject => typeof related?.attributes?.['name'] === 'string' && related.attributes['name'] !== '')
        .map((related) => ({ slug: String(related.attributes['slug'] ?? related.id), name: related.attributes['name'] }));

    return {
      id: Number(resource.id),
      slug: a['slug'],
      name: a['name'] ?? null,
      summary: a['summary'] ?? null,
      originalRequest: a['original_request'] ?? null,
      description: a['description'] ?? null,
      solution: a['solution'] ?? null,
      status: a['status'],
      stage: a['stage'] ?? null,
      statusReason: a['status_reason'] ?? null,
      statusChangedAt: a['status_changed_at'] ? new Date(a['status_changed_at']) : null,
      createdAt: a['created_at'] ? new Date(a['created_at']) : null,
      pending: !!a['pending'],
      visibility: a['visibility'] === 'hub' ? 'hub' : 'public',
      platformWide: !!a['platform_wide'],
      hub: a['hub'] ?? null,
      duplicateOf: a['duplicate_of'] ?? null,
      followsOnFrom: a['follows_on_from'] ?? null,
      origin: a['origin'] ?? 'self_service',
      initiatorName: a['initiator_name'] ?? null,
      likesCount: a['likes_count'] ?? 0,
      liked: !!a['liked'],
      following: !!a['following'],
      commentsCount: a['comments_count'] ?? 0,
      relevance: a['relevance'] ?? [],
      topics: named('topics'),
      tags: named('tags'),
      cultures: named('cultures'),
      can: {
        like: !!a['can']?.['like'],
        comment: !!a['can']?.['comment'],
        follow: !!a['can']?.['follow'],
        withdraw: !!a['can']?.['withdraw'],
      },
      requestedLocale: resource.meta?.['requested_locale'] ?? null,
      servedLocale: resource.meta?.['served_locale'] ?? null,
      translationState: (resource.meta?.['translation_state'] as FeatureRequestTranslationState) ?? null,
      worthPolling: !!resource.meta?.['worth_polling'],
      inputs: (Array.isArray(a['inputs']) ? a['inputs'] : []).map((offer: Record<string, any>) => ({
        slug: String(offer['slug']),
        name: offer['name'] ?? null,
        task: offer['task'] ?? null,
        creditsMin: offer['credits_min'] ?? null,
        creditsMax: offer['credits_max'] ?? null,
      })),
      thumbnail: FeatureRequestService.thumbnail(a['thumbnail']),
    };
  }

  private intervalFor(attempt: number): number {
    return Math.min(FeatureRequestService.POLL_INTERVAL_SECONDS * attempt, FeatureRequestService.POLL_MAXIMUM_INTERVAL_SECONDS) * 1000;
  }

  private fail(error: HttpErrorResponse): Observable<never> {
    const status = error?.status ?? 0;
    const detail: string | undefined = error?.error?.errors?.[0]?.detail;
    const notInvolved = status === 403 && error?.error?.errors?.[0]?.code === INVOLVEMENT_REQUIRED;
    // Only "get involved" is Ideas' own; every other failure says what any feature
    // would, in the shared keys `ErrorService` uses.
    const translationKey =
      notInvolved ? 'ideas.get_involved'
      : status === 404 ? 'common.not_found'
      : status === 403 ? 'error.not_allowed'
      : status === 422 ? 'error.data_invalid'
      : status === 401 ? 'authentication.not_authenticated'
      : 'error.not_done_try_again';

    const failure: FeatureRequestError = { status, translationKey, detail, notInvolved };

    return throwError(() => failure);
  }
}
